import React, { Suspense, lazy } from 'react';
/**
 * PortalSwitch — Role-based portal rendering
 *
 * Extracted from App.tsx in R33 (DDD Fase 6 — App Shell decomposition).
 * Renders the appropriate portal shell (Teacher, Parent, Admin) based on
 * the active account's role. Portal visibility is controlled by
 * currentNavView in AppContent — this component only handles which
 * portal shell to render.
 */
import type { AccountsState, Account } from '../accounts';
import type { AppState } from './types';
import type { Question } from '../content';
import type { GroupId } from '../config/systemConfig';
const TeacherShell = lazy(() => import('../portals/teacher-v2/TeacherShellV2').then(m => ({
  default: m.TeacherShellV2
})));
const ParentShell = lazy(() => import('../portals/parent-v2/ParentShellV2').then(m => ({
  default: m.ParentShellV2
})));
const AdminShell = lazy(() => import('../portals/admin/AdminShell').then(m => ({
  default: m.AdminShell
})));
interface PortalSwitchProps {
  activeAccount: Account | null;
  accounts: AccountsState;
  state: AppState;
  selectedGroup: GroupId;
  questions: Question[];
  onNavigateToLearner: (learnerId: string) => void;
  onLogout: () => void;
  onSwitchAccount: (accountId: string) => void;
  onNavigate: (view: string) => void;
  getLearnerData: (learnerId: string) => AppState | null;
  getLearnerModuleMastery: (learnerId: string, moduleId: string) => number;
}
const SPINNER_COLORS: Record<string, string> = {
  orange: 'border-orange-200 border-t-orange-500',
  emerald: 'border-emerald-200 border-t-emerald-600'
};
function PortalSpinner({
  color
}: {
  color: string;
}) {
  const colorClasses = SPINNER_COLORS[color] || SPINNER_COLORS.orange;
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-id="element-675" data-mp="23zius4hqohau">
      <div className={`w-8 h-8 border-4 rounded-full animate-spin ${colorClasses}`} data-id="element-676" data-mp="3af3alwfz5xy3" />
      
    </div>;
}
export function PortalSwitch(props: PortalSwitchProps): React.ReactElement | null {
  const {
    activeAccount,
    accounts,
    state,
    selectedGroup,
    questions,
    onNavigateToLearner,
    onLogout,
    onSwitchAccount,
    onNavigate,
    getLearnerData,
    getLearnerModuleMastery
  } = props;
  /*
   ── "Naar leerlingomgeving" is weg bij ouder en leerkracht ────────────────
    Die knop riep `onSwitchAccount(eersteLeerlingOpDitApparaat)` aan: een
   stille accountwissel naar het account van een KIND, zonder wachtwoord en
   zonder enige controle. Drie dingen zijn daar mis mee, en ze stapelen:
    1. Het is geen navigatie maar een identiteitswissel. Alles wat daarna
      gebeurt — geoefend, XP verdiend, instellingen gewijzigd — staat op naam
      van het kind. Het kind kan achteraf niet zien dat iemand anders het deed.
   2. "De eerste leerling op dit apparaat" is niet noodzakelijk het eigen
      kind. Op een gedeeld gezins- of klasapparaat is dat het kind dat er
      toevallig als eerste in de lijst staat.
   3. Het omzeilt precies de grens die de rest van dit project bewaakt: een
      ouder ziet voortgang via zijn eigen portaal, een leerkracht via de klas.
      Inloggen ALS het kind is een vierde route die nergens is afgewogen.
    Dit is dezelfde fout als de accountwisselaar die we eerder verwijderd
   hebben. Wil een volwassene het kind helpen, dan logt het kind zelf in.
    BELANGRIJK — dit raakt NIET het "meekijken" dat wél een besluit is.
   `onNavigateToLearner` zet `viewingLearnerId`, een APARTE staat naast
   `activeAccountId`: de volwassene blijft ingelogd als zichzelf, ziet een
   banner "Je bekijkt het dashboard van …", en het is read-only tegen echte
   data (SUPABASE_SECURITY_MODEL.md §3 — alleen de leerling zelf schrijft).
   Dat blijft. Wat weg is, is de vierde route die daar naast lag: écht
   inloggen als het kind, waarbij alles wat je doet op zijn naam komt.
    De prop is óók verwijderd en niet alleen losgekoppeld. Een optionele prop
   die nergens meer wordt doorgegeven is één regel verwijderd van
   teruggekeerd gedrag — precies hoe de accountwisselaar eerder terugkwam
   nadat alleen de barrel-export was weggehaald.
   */
  // Teacher portal
  if (activeAccount?.role === 'teacher') {
    return <Suspense fallback={<PortalSpinner color="orange" data-id="element-678" data-mp="1tcr4mt6w66fg" />} data-id="element-677" data-mp="1y9rbv7fh9f85">
        <TeacherShell onNavigateToLearner={onNavigateToLearner} onLogout={onLogout} data-id="element-679" data-mp="1tdsonn5vy39b" />
        
      </Suspense>;
  }
  // Parent portal
  if (activeAccount?.role === 'parent') {
    return <Suspense fallback={<PortalSpinner color="emerald" data-id="element-681" data-mp="2m055319stk4b" />} data-id="element-680" data-mp="2m1om4u3a3z75">
        <ParentShell onNavigateToLearner={onNavigateToLearner} onLogout={onLogout} data-id="element-682" data-mp="2lz2dss8kdfnv" />
        
      </Suspense>;
  }
  // Admin portal
  if (activeAccount?.role === 'admin') {
    const accountsList = Object.values(accounts.accountsById);
    return <Suspense fallback={<PortalSpinner color="orange" data-id="element-684" data-mp="280vrm9cz9441" />} data-id="element-683" data-mp="282gmk037ep2f">
        <AdminShell accounts={accounts} getLearnerData={getLearnerData} getLearnerModuleMastery={getLearnerModuleMastery} selectedGroup={selectedGroup} onNavigate={view => onNavigate(view as string)} onNavigateToLearner={onNavigateToLearner} allAccounts={accountsList} activeAccountId={accounts.activeAccountId} onSwitchAccount={onSwitchAccount} onLogout={onLogout} questions={questions} state={state} data-id="element-685" data-mp="11llaquhrl4ym" />
        
      </Suspense>;
  }
  return null;
}